import { CircularProgress } from '@material-ui/core';
import { modeUIContext, setIsEditingForm, showErrorNotification, showSuccessNotification } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce from 'immer';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { BaseInfoCompProps, capitalizeAll, FieldInputType, GenericInfoComp, SelectInputOption } from '@moderepo/mode-app-components';
import { fetchAgentProjectById, ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { userAppDataContext, selectHomeKVStore, fetchHomeKVStore } from '@moderepo/mode-data-state-user-app';
import { Agent } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { Dashboard, HOME_DASHBOARDS_KV_KEY, isHomeDashboardsKeyValuePair, isProjectDashboardsSettings } from './dashboardTypes';
import { DashboardSettingsCompProps, DashboardSettingsUpdatableInputs }
    from '../../components/dashboard/DashboardSettingsComp';
import { addHomeDashboard, addProjectDashboard, DashboardLogicApiCallResponse, updateHomeDashboard, updateProjectDashboard } from './dashboardLogic';
import { createIconOptions } from './dashboardUtils';


interface InputErrors {
    title?: string | undefined;
    subtitle?: string | undefined;
    icon?: string | undefined;
}


interface DashboardSettingsContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly dashboardId?: number;                              // will be undefined if this container is used for creating new dashboard
    readonly newDashboardHandlers?: {                           // Only available if this container is used for creating new dashboard
        readonly onNewDashboardCreated: ()=> void;
        readonly onNewDashboardCanceled: ()=> void;
    }

    // Props to pass to the DashboardSettingsComp if there are any custom style
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;

    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly homeId?: number | undefined;
    }
}


/**
 * This container is a wrapper for the DashboardSettingsComp. It contains the LOGICS for the component.
 */
export const DashboardSettingsContainer: React.FC<DashboardSettingsContainerProps> = (props: DashboardSettingsContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;


    const selectedDashboard = useMemo(() => {
        // If there is a props.dashboardId, we need to get the dashboard object
        if (props.dashboardId) {
            if (props.routeContext?.homeId) {
                // Get the list dashboards from the Home KV and then try to find the dashboard with ID equal props.dashboardId
                const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
                if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                    const dashboards = dashboardKV.value;
                    return dashboards.find((db) => {
                        return db.id === props.dashboardId;
                    });
                }
            } else if (project && isProjectDashboardsSettings(project?.supportToolSettings.dashboardSettings)) {
                const { dashboards } = project.supportToolSettings.dashboardSettings;
                return dashboards.find((dashboard) => {
                    return dashboard.id === props.dashboardId;
                });
            }
        }

        return undefined;
    }, [appDataState, project, props.dashboardId, props.routeContext]);

    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<InputErrors>({});
    const isNewDashboard = props.dashboardId === undefined;



    /**
     * Drop down options to be used in the Icon input field
     */
    const iconInputOptions = useMemo((): readonly SelectInputOption<string>[] => {
        return createIconOptions(true, capitalizeAll(trans('label.none')));
    }, [trans]);


    /**
     * This useEffect is used for fetching the Home KV store which is used for storing the provided homeId's dashboard. We need to load
     * the home Dashboard in order to get the widget settings for the selected widget
     */
    useEffect(() => {
        if (props.routeContext?.homeId) {
            appDataDispatch(fetchHomeKVStore(props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY), true, true).catch(() => {
                // ignore error because appDataDispatch already handled the error
            });
        } else {
            dataDispatch(fetchAgentProjectById(loggedInAgent.id, props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [appDataDispatch, dataDispatch, loggedInAgent.id, props.projectId, props.routeContext]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.dashboardId is undefined which is when user want to create a Dashboard
     */
    useEffect(() => {
        if (isNewDashboard) {
            onChangeEditingMode(true);
        }
    }, [isNewDashboard, onChangeEditingMode]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({});

        // also change global edit mode
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    /**
     * On user form input change, we will clear the error message for that field.
     */
    const onFormInputChange = useCallback((fieldName: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));
    }, [inputErrors]);



    // Create a new Dashboard with the given inputs. We can assume the inputs are validated
    const onCreateNewDashboard = useCallback(async (inputs: DashboardSettingsUpdatableInputs) => {
        const { title, subtitle, icon } = inputs;

        if (title) {

            const onCompleted = (result: DashboardLogicApiCallResponse) => {
                if (result.success) {
                    closeEditing();
                    props.newDashboardHandlers?.onNewDashboardCreated();
                } else if (result.errorMessageKey) {
                    uiDispatch(showErrorNotification(result.errorMessageKey));
                }
            };

            const newDashboard = {
                id     : Date.now(),
                title,
                subtitle,
                icon,
                widgets: [],
            };

            if (props.routeContext?.homeId) {
                addHomeDashboard(appDataState, appDataDispatch, props.routeContext?.homeId, newDashboard).then(onCompleted);
            } else {
                addProjectDashboard(dataState, dataDispatch, props.projectId, loggedInAgent.id, newDashboard).then(onCompleted);
            }
        }
    }, [appDataDispatch, appDataState, closeEditing, dataDispatch, dataState, loggedInAgent, props.newDashboardHandlers, props.projectId,
        props.routeContext, uiDispatch]);



    // Update an existing Dashboard with the given inputs. We can assume the inputs are validated
    const onUpdateDashboard = useCallback(async (inputs: DashboardSettingsUpdatableInputs, dashboard: Dashboard) => {
        const { title, subtitle, icon } = inputs;

        if (title && (title !== dashboard.title || subtitle !== dashboard.subtitle || icon !== dashboard.icon)) {

            const onCompleted = (result: DashboardLogicApiCallResponse) => {
                if (result.success) {
                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'dashboard.label' }));
                    closeEditing();
                } else if (result.errorMessageKey) {
                    uiDispatch(showErrorNotification(trans(result.errorMessageKey)));
                }
            };

            const updatedDashboard = {
                ...dashboard,
                title   : title !== dashboard.title ? title : dashboard.title,
                subtitle: subtitle !== dashboard.subtitle ? subtitle : dashboard.subtitle,
                icon    : icon !== dashboard.icon ? icon : dashboard.icon,
            };

            if (props.routeContext?.homeId) {
                updateHomeDashboard(appDataState, appDataDispatch, props.routeContext.homeId, updatedDashboard).then(onCompleted);
            } else {
                updateProjectDashboard(dataState, dataDispatch, props.projectId, loggedInAgent.id, updatedDashboard).then(onCompleted);
            }
        } else {
            // input values are the same so no need to make api call
            closeEditing();
        }
    }, [appDataDispatch, appDataState, closeEditing, dataDispatch, dataState, loggedInAgent.id, props.projectId, props.routeContext, trans,
        uiDispatch]);



    /**
     * Handler for when the user click Save input from the Dashboard form.
     * If we are in creating new dashboard mode, call onCreateNewDashboard. Else update the current dashboard
     */
    const onFormSubmit = useCallback(async (inputs: DashboardSettingsUpdatableInputs) => {
        if (!project) {
            return;
        }

        const newInputErrors: InputErrors = ({});
        const { title } = inputs;


        // Only title is required so we only need to validate title
        if (!title) {
            newInputErrors.title = 'error.input.generic.empty';
        }

        setInputErrors(newInputErrors);

        // Go through all the values in the newErrors object to find a non undefined value. If there is at least 1 non undefined value,
        // that mean there is an error.
        const hasErrors = Object.values(newInputErrors).some((error) => {
            return error !== undefined;
        });

        // If there are not errors, we can continue CREATE a dashboard or updating an existing dashboard
        if (!hasErrors) {
            if (isNewDashboard) {
                onCreateNewDashboard(inputs);
            } else if (selectedDashboard) {
                onUpdateDashboard(inputs, selectedDashboard);
            }
        }
    }, [project, isNewDashboard, selectedDashboard, onCreateNewDashboard, onUpdateDashboard]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (props.newDashboardHandlers !== undefined) {
            props.newDashboardHandlers.onNewDashboardCanceled();
        }
    }, [props.newDashboardHandlers, closeEditing]);



    /**
     * Create the input settings for the DashboardSettingsComp and cache it
     */
    const inputsSettings = useMemo((): DashboardSettingsCompProps['fields'] => {
        return {
            title: {
                label       : capitalizeAll(trans('field.title.label')),
                displayValue: selectedDashboard?.title,
                value       : selectedDashboard?.title,
                required    : true,
                editable    : true,
                error       : inputErrors.title ? trans(inputErrors.title) : '',
            },
            subtitle: {
                label       : capitalizeAll(trans('field.subtitle.label')),
                displayValue: selectedDashboard?.subtitle,
                value       : selectedDashboard?.subtitle,
                required    : false,
                editable    : true,
                error       : inputErrors.subtitle ? trans(inputErrors.subtitle) : '',
            },
            icon: {
                controlType : FieldInputType.AUTOCOMPLETE,
                label       : capitalizeAll(trans('field.icon.label')),
                displayValue: selectedDashboard?.icon,
                value       : selectedDashboard?.icon,
                required    : false,
                editable    : true,
                options     : iconInputOptions,
                error       : inputErrors.icon ? trans(inputErrors.icon) : '',
            },
        };
    }, [selectedDashboard, trans, inputErrors.title, inputErrors.subtitle, inputErrors.icon, iconInputOptions]);


    return (
        <>
            {project && (selectedDashboard || isNewDashboard) && (
                <GenericInfoComp<DashboardSettingsCompProps>
                    className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
                    formProps={props.infoCompProps?.formProps}
                    isEditing={isEditing}
                    title={capitalizeAll(trans(isNewDashboard ? 'dashboard.settings_comp.create_dashboard.title' : 'dashboard.settings_comp.title'))}
                    icon="dashboard"
                    fields={inputsSettings}
                    actions={{
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : isNewDashboard ? capitalizeAll(trans('label.create')) : capitalizeAll(trans('label.save')),
                            onClick: onFormSubmit,
                        },
                    }}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onFormInputChange}
                />
            )}

            {(!project || (!isNewDashboard && !selectedDashboard)) && (
                <CircularProgress />
            )}
        </>

    );
};
